import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, Bell, BellOff, BookmarkPlus, AlertCircle, Tag, MapPin } from 'lucide-react';
import { Loader } from './ui/Loader';
import { API_BASE } from '../utils/supabase/info';
import { toast } from '../utils/toast';
import { handleApiError } from '../utils/api-error-handler';
import { devError } from '../utils/logger';


interface CreateSavedSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  searchTerms: string[];
  counties: string[];
  accessToken: string;
  currentSavedSearchCount: number;
  onSuccess: () => void;
  onNavigateToSubscription?: () => void;
}

export function CreateSavedSearchModal({
  isOpen,
  onClose,
  searchTerms,
  counties,
  accessToken,
  currentSavedSearchCount,
  onSuccess,
  onNavigateToSubscription,
}: CreateSavedSearchModalProps) {
  const [searchName, setSearchName] = useState('');
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!searchName.trim()) {
      toast.error('Navn mangler', 'Gi søket et beskrivende navn');
      return;
    }

    if (searchName.length > 50) {
      toast.error('Navn for langt', 'Maks 50 tegn');
      return;
    }

    setSaving(true);

    try {
      const response = await fetch(
        `${API_BASE}/saved-searches`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`
          },
          body: JSON.stringify({
            name: searchName,
            searchTerms,
            counties,
            notificationsEnabled
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        handleApiError(errorData, () => {
          onNavigateToSubscription?.();
        });
        return;
      }

      toast.success(
        'Søk lagret!',
        notificationsEnabled 
          ? 'Du vil motta varsler om nye matches'
          : 'Søket er lagret uten varsler'
      );

      onSuccess();
      onClose();
      setSearchName('');
      setNotificationsEnabled(true);
    } catch (error) {
      devError('Error saving search:', error);
      toast.error('Noe gikk galt', 'Kunne ikke lagre søk');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/20 backdrop-blur-md"
            style={{ zIndex: 'var(--z-overlay)' }}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-x-4 top-1/2 -translate-y-1/2 xl:left-1/2 xl:-translate-x-1/2 xl:max-w-lg bg-white rounded-2xl shadow-2xl max-h-[85vh] overflow-y-auto"
            style={{ zIndex: 'var(--z-modal)' }}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                  <BookmarkPlus className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h2 className="font-semibold text-gray-900">Lagre søk</h2>
                  <p className="text-xs text-gray-600">
                    {currentSavedSearchCount}/3 lagrede søk
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center transition-colors"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-5">
              {/* Warning if at limit */}
              {currentSavedSearchCount >= 3 && (
                <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-900">
                    <p className="font-medium">Maks antall nådd</p>
                    <p className="text-amber-700 mt-1">
                      Du har allerede 3 lagrede søk. Slett et eksisterende søk for å lagre et nytt.
                    </p>
                  </div>
                </div>
              )}

              {/* Name input */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Navn på søk
                </label>
                <input
                  type="text"
                  value={searchName}
                  onChange={(e) => setSearchName(e.target.value)}
                  placeholder="F.eks. 'React utviklere Oslo'"
                  maxLength={50}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  disabled={saving || currentSavedSearchCount >= 3}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {searchName.length}/50 tegn
                </p>
              </div>

              {/* Search terms display */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  <Tag className="w-4 h-4 inline mr-1" />
                  Søkeord
                </label>
                <div className="flex flex-wrap gap-2">
                  {searchTerms.map((term, index) => (
                    <span
                      key={index}
                      className="px-3 py-1.5 bg-purple-100 text-purple-700 rounded-full text-sm font-medium"
                    >
                      {term}
                    </span>
                  ))}
                </div>
              </div>

              {/* Counties display */}
              {counties.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    <MapPin className="w-4 h-4 inline mr-1" />
                    Fylker
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {counties.map((county, index) => (
                      <span
                        key={index}
                        className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-full text-sm font-medium"
                      >
                        {county}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Notifications toggle */}
              <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
                <input
                  type="checkbox"
                  id="notifications-toggle"
                  checked={notificationsEnabled}
                  onChange={(e) => setNotificationsEnabled(e.target.checked)}
                  disabled={saving || currentSavedSearchCount >= 3}
                  className="w-5 h-5 rounded border-gray-300 text-purple-600 focus:ring-purple-500 mt-0.5"
                />
                <div className="flex-1">
                  <label
                    htmlFor="notifications-toggle"
                    className="flex items-center gap-2 text-sm font-medium text-gray-900 cursor-pointer"
                  >
                    <Bell className="w-4 h-4 text-gray-600" />
                    Motta varsler om nye matches
                  </label>
                  <p className="text-xs text-gray-600 mt-1">
                    Vi sender deg et push-varsel når nye talenter matcher dette søket
                  </p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center gap-3 p-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={onClose}
                disabled={saving}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-xl font-medium text-gray-700 hover:bg-white transition-colors disabled:opacity-50"
              >
                Avbryt
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !searchName.trim() || currentSavedSearchCount >= 3}
                className="flex-1 px-4 py-3 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {saving ? (
                  <>
                    <Loader size="sm" className="text-white/90" />
                    Lagrer...
                  </>
                ) : (
                  <>
                    <BookmarkPlus className="w-4 h-4" />
                    Lagre søk
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
